<script setup lang="ts">
import { Button } from "@/components/ui/button";
import { AutoForm } from "@/ui-plus/auto-form";
import { useFormPlus } from "@/ui-plus/form/useFormPlus";
import { toTypedSchema } from "@vee-validate/zod";
import { useForm } from "vee-validate";
import { z } from "zod";
import { LOCAL_STORAGE_KEYS } from "@auth-frontend-vue/const";
import { showTurnstile } from "@/ui-plus/dialog/showTurnstile";
import { useDialog } from "@/ui-plus/dialog/use-dialog";
import { Loader2 } from "lucide-vue-next";
import PhoneSignupVerifyForm from "@auth-frontend-vue/components/forms/auth-forms/PhoneSignupVerifyForm.vue";
import { registerPhone } from "@auth-frontend-vue/lib/auth.actions";
import { onMounted } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const props = defineProps<{
	phoneNumber: string;
}>();

const emit = defineEmits<{
	useCodeInstead: [];
}>();

const dialog = useDialog();

const schema = z.object({
	name: z.string().min(1, { message: "min 1" }).max(255, { message: "max 255" }),
	password: z.string().min(8, { message: "min 8, use only StroNg_P@ssw0rd" }).max(255, { message: "max 255" }),
	confirm: z.string(),
}).refine(data => data.password === data.confirm, {
	message: t('passwordsMustMatch'),
	path: ['confirm'],
});

const form = useForm({
	validationSchema: toTypedSchema(schema)
});

const { isLoading, globalError, handleError, withSubmit } = useFormPlus(form, {
	persistenceFields: {
		name: LOCAL_STORAGE_KEYS.REMEMBER_LAST_NAME
	}
});

onMounted(async () => {
	await new Promise(resolve => setTimeout(resolve, 500));
	const nameInput = document.querySelector<HTMLInputElement>('[name="name"]');
	if (nameInput) {
		nameInput.focus();
	}
})

async function onSubmit(values: z.infer<typeof schema>) {
	await withSubmit(async () => {
		const { error } = await registerPhone({
			phoneNumber: props.phoneNumber,
			password: values.password,
			name: values.name,
			turnstile: await showTurnstile(import.meta.env.PUBLIC_TURNSTILE_SITE_KEY)
		});
		if (error) {
			handleError(error, form)
		} else {
			dialog.show<{ phoneNumber: string, code: string }>({
				title: "Please verify your phone",
				closeable: false,
				component: PhoneSignupVerifyForm,
				props: {
					phoneNumber: props.phoneNumber,
				},
				onSuccess: async (_result) => { }
			});
		}
	})
}
</script>

<template>
	<div>
		<h1 class="text-2xl font-bold">{{ $t('createAccount') }}</h1>
		<p class="text-sm text-muted-foreground mt-1">{{ $t('fillDetailsToContinue') }}</p>

		<AutoForm class="space-y-6 mt-6"
				  :schema="schema"
				  :form="form"
				  @submit="onSubmit"
				  :field-config="{
					name: {
						inputProps: {
							autocomplete: 'name',
							autofocus: true,
						}
					},
					password: {
						inputProps: {
							type: 'password',
							autocomplete: 'new-password',
						}
					},
					confirm: {
						label: t('confirmPassword'),
						inputProps: {
							type: 'password',
							autocomplete: 'new-password',
						},
					},
				}">
			<input type="tel"
				   :value="phoneNumber"
				   name="username"
				   autocomplete="username"
				   readonly
				   tabindex="-1"
				   aria-hidden="true"
				   class="sr-only" />

			<div class="text-sm font-medium text-destructive"
				 v-if="globalError">
				{{ globalError }}
			</div>
			<div>
				<Button type="submit"
						:disabled="isLoading"
						class="w-full my-6">
					<Loader2 v-if="isLoading"
							 class="animate-spin" />
					{{ $t('signup') }}
				</Button>
			</div>
		</AutoForm>
		<div class="text-center">
			<button type="button"
					@click="emit('useCodeInstead')"
					class="inline-block text-sm underline">
				{{ $t('signUpWithCode') }}
			</button>
		</div>
	</div>
</template>
